import React, { useState, useEffect, useCallback } from "react";
import { Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getLoginSignupUrl, isNafdaqLoginHost, UNAUTHORIZED_PARTNER_MESSAGE } from "constants/access";
import { authService } from "services";
import { FiEye, FiEyeOff, FiArrowLeft } from "react-icons/fi";
import LoginOtpStep from "./LoginOtpStep";
import AnimatedLogo from "Common/AnimatedLogo/AnimatedLogo";
import { BRAND_WEBSITE_URL } from "constants/brand";

import './Login.scss';

const OTP_SESSION_TTL_MS = 10 * 60 * 1000;

const SignIn = () => {
  const [err, setError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [passwordshow, setpasswordshow] = useState(false);
  const [step, setStep] = useState("credentials");
  const [data, setData] = useState({ email: "", password: "" });
  const { email, password } = data;
  const [otpSession, setOtpSession] = useState(null);
  const [ipInfo, setIpInfo] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const storedLoginError = sessionStorage.getItem("loginError");
    if (storedLoginError) {
      setError(storedLoginError);
      sessionStorage.removeItem("loginError");
    }

    const fetchIPInfo = async () => {
      try {
        const response = await fetch('https://ipinfo.io/json?token=52a7645817b83a');
        const data = await response.json();
        setIpInfo(data);
        if (data.country === 'MA' && !isNafdaqLoginHost()) {
          window.location.href = `https://login-ma.godocm.com`;
        }
      } catch (error) {
        console.error('Error fetching IP information:', error);
      }
    };
    fetchIPInfo();
  }, []);

  const signupUrl = getLoginSignupUrl({ countryCode: ipInfo?.country });

  const hasValidOtpSession = useCallback(() => {
    if (!otpSession) return false;
    if (otpSession.email !== email || otpSession.password !== password) return false;
    return Date.now() - otpSession.sentAt < OTP_SESSION_TTL_MS;
  }, [otpSession, email, password]);

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
    setError("");

    if (name === "email" || name === "password") {
      const sessionMismatch = otpSession && (
        (name === "email" && value !== otpSession.email) ||
        (name === "password" && value !== otpSession.password)
      );
      if (sessionMismatch) {
        setOtpSession(null);
        if (step === "otp") setStep("credentials");
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && step === "credentials" && !sendingOtp && !verifyingOtp) {
      handleCredentialsSubmit(e);
    }
  };

  const verifyLogin = (otpValue) => {
    setVerifyingOtp(true);
    setOtpError("");
    const redirectPath = localStorage.getItem("redirectFrom") || "/dashboard";

    authService
      .login(email, password, otpValue)
      .then(() => {
        toast.dismiss();
        localStorage.removeItem("redirectFrom");
        setOtpSession(null);
        navigate(redirectPath, { replace: true });
      })
      .catch((err) => {
        const errorMessage =
          err?.message ||
          err?.response?.data?.message ||
          "Login failed. Please check your details and try again.";
        const isUnauthorizedPartner =
          err?.code === "UNAUTHORIZED_PARTNER" ||
          errorMessage === UNAUTHORIZED_PARTNER_MESSAGE;

        if (isUnauthorizedPartner) {
          setOtpSession(null);
          setStep("credentials");
          setError(errorMessage);
          return;
        }

        setOtpError(errorMessage);
      })
      .finally(() => {
        setVerifyingOtp(false);
      });
  };

  const requestOtp = async (forceResend = false) => {
    if (!forceResend && hasValidOtpSession()) {
      setStep("otp");
      setError("");
      setOtpError("");
      return true;
    }

    setSendingOtp(true);
    setError("");
    setOtpError("");
    try {
      await authService.sendLoginOtp(email, password);
      setOtpSession({ email, password, sentAt: Date.now() });
      setStep("otp");
      return true;
    } catch (error) {
      const apiMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Failed to send OTP";
      const isInvalidCredentials = /invalid credentials/i.test(apiMessage);
      const isCooldown = /please wait/i.test(apiMessage);

      if (isCooldown && hasValidOtpSession()) {
        setStep("otp");
        setOtpError("");
        return true;
      }

      const errorMessage = isInvalidCredentials ? "Wrong credentials" : apiMessage;
      if (step === "otp") {
        setOtpError(errorMessage);
        if (isInvalidCredentials) {
          setStep("credentials");
          setOtpSession(null);
        }
      } else {
        setError(errorMessage);
      }
      return false;
    } finally {
      setSendingOtp(false);
    }
  };

  const handleCredentialsSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (sendingOtp || verifyingOtp) return;
    if (!email) {
      setError("Please enter trading account first");
      return;
    }
    if (!password) {
      setError("Please enter password");
      return;
    }
    requestOtp(false);
  };

  const handleResendOtp = () => requestOtp(true);

  const handleBackToCredentials = () => {
    setStep("credentials");
    setOtpError("");
  };

  return (
    <div>
      <div className="loginBackgroundColor">
        <div className="overflow-hidden">
          <div>
            <div className="row">
              <div className="col-lg-7 p-0 d-none d-lg-block">
                <div className="leftImage"></div>
              </div>
              <div className="col-12 col-lg-5">
                <div className="flexBlock blockWapper">
                  <div className="logoBlock">
                    <a href={BRAND_WEBSITE_URL} target="_blank" rel="noreferrer">
                      <AnimatedLogo />
                    </a>
                  </div>
                  <div className="login-form validate-form">
                    <form className="login-form validate-form" onSubmit={(e) => e.preventDefault()}>
                      <span className="login100-form-title">
                        {step === "otp" ? "Login Verification" : "Welcome Back"}
                      </span>

                      <div className={`login-form__panel ${step === "credentials" ? "is-active" : "is-hidden"}`}>
                        {err && <Alert variant="danger">{err}</Alert>}
                        <div className="emailBlock">
                          <label htmlFor="email">Trading Account</label>
                          <input
                            type="email"
                            name="email"
                            id="email"
                            placeholder="Enter your trading account"
                            value={email}
                            onChange={changeHandler}
                            onKeyDown={handleKeyDown}
                            required
                          />
                        </div>
                        <div className="passwordBlock">
                          <label htmlFor="password">Password</label>
                          <div className="passwordWapper">
                            <input
                              type={passwordshow ? "text" : "password"}
                              name="password"
                              id="password"
                              placeholder="Enter your password"
                              value={password}
                              onChange={changeHandler}
                              onKeyDown={handleKeyDown}
                              required
                            />
                            {passwordshow
                              ? <FiEye className="fiEye" onClick={() => setpasswordshow(!passwordshow)} />
                              : <FiEyeOff className="fiEye" onClick={() => setpasswordshow(!passwordshow)} />}
                          </div>
                        </div>
                        <div className="credentialInfo">
                          <div className="form-check">
                            <input className="form-check-input" type="checkbox" value="" id="flexCheckDefault" />
                            <label className="form-check-label" htmlFor="flexCheckDefault">
                              Remember me
                            </label>
                          </div>
                          <div>
                            <a href="/forget-password">Forgot Password</a>
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={sendingOtp || verifyingOtp}
                          onClick={handleCredentialsSubmit}
                        >
                          {sendingOtp ? "Verifying..." : "Submit"}
                        </button>
                      </div>

                      <div className={`login-form__panel ${step === "otp" ? "is-active" : "is-hidden"}`}>
                        {step === "otp" && (
                          <LoginOtpStep
                            onVerify={verifyLogin}
                            onResend={handleResendOtp}
                            loading={verifyingOtp}
                            resendLoading={sendingOtp}
                            error={otpError}
                            onClearError={() => setOtpError("")}
                            otpSentAt={otpSession?.sentAt}
                          />
                        )}
                      </div>
                    </form>
                  </div>
                  <div className="bottomLink">
                    <div className="bottomLink__row">
                      {step === "otp" && (
                        <button
                          type="button"
                          className="login-back-arrow"
                          onClick={handleBackToCredentials}
                          disabled={sendingOtp || verifyingOtp}
                          aria-label="Back to login"
                        >
                          <FiArrowLeft />
                        </button>
                      )}
                      <a className="linkHover" target="_blank" rel="noreferrer" href={signupUrl}>
                        Don't have an account? <span>Sign Up</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
